import { useEffect, useState } from 'react';
import { useAuthStore } from '@/store/auth';
import { bootstrapSilentRefresh } from '@/lib/api';
import { shouldRestoreAtStartup, wasExplicitlySignedOut } from '@/lib/sessionRestore';

/** Longest the splash waits for the startup restore before falling to
 *  Login. 10 s, not the driver app's 4 s: a slow refresh then /auth/me
 *  should still restore, and a hung one must never trap the splash. */
const BOOTSTRAP_TIMEOUT_MS = 10_000;

/**
 * The one restore per page load. Module-level so React StrictMode's
 * double-run mount effect joins the same attempt (and keeps the splash)
 * instead of starting a second refresh. A full page load re-inits it.
 */
let bootstrapRun: Promise<void> | null = null;
let bootstrapSettled = false;

function startupWantsRestore(): boolean {
  return shouldRestoreAtStartup({
    isAuthenticated: useAuthStore.getState().isAuthenticated,
    alreadyAttempted: bootstrapRun !== null,
    signedOutOnPurpose: wasExplicitlySignedOut(),
    pathname: window.location.pathname,
  });
}

function runBootstrap(): Promise<void> {
  const controller = new AbortController();
  const capped = new Promise<void>((resolve) => {
    // The cap resolves the splash even if /auth/me is still out; aborting
    // also makes a late result discard itself rather than flip Login back.
    const timer = window.setTimeout(() => {
      controller.abort();
      resolve();
    }, BOOTSTRAP_TIMEOUT_MS);
    bootstrapSilentRefresh(controller.signal)
      .catch(() => false)
      .finally(() => {
        window.clearTimeout(timer);
        resolve();
      });
  });
  return capped.then(() => { bootstrapSettled = true; });
}

/**
 * Startup restore from the 30-day admin refresh cookie, run before the
 * auth gate picks Login or the app. Returns whether to hold on a splash.
 *   - saved session in this tab        : no splash, no refresh
 *   - no session, cookie still good    : splash, refresh, /auth/me, app
 *   - no session, no cookie or refused : splash, then Login
 *   - signed out on purpose in this tab, or a pre-auth route
 *     (invite, magic-link verify, reset, MFA) : no restore
 */
export function useBootstrapAuth(): boolean {
  const [isBootstrapping, setIsBootstrapping] = useState(
    () => (bootstrapRun ? !bootstrapSettled : startupWantsRestore()),
  );

  useEffect(() => {
    if (!bootstrapRun) {
      if (!startupWantsRestore()) {
        setIsBootstrapping(false);
        return;
      }
      bootstrapRun = runBootstrap();
    }
    let active = true;
    void bootstrapRun.then(() => {
      if (active) setIsBootstrapping(false);
    });
    return () => { active = false; };
  }, []);

  return isBootstrapping;
}
